import { Map as ImmutableMap } from 'immutable';
import { createSelector } from 'reselect';

import { accountsSelectors } from '../accounts';
import { categoriesSelectors } from '../categories';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from '../chart-of-accounts';
import { selectorsHelpers } from '../core';
import { transactionsSelectors, transactionsUtils } from '../transactions';

import { REDUCER_KEY } from './memorizedRulesReducer';

export const getStore = (state) => state[REDUCER_KEY];
export const getLoadPending = (state) => getStore(state).loadPending;
export const getIsLoading = (state) => getStore(state).isLoading;
export const getError = (state) => getStore(state).error;
export const getResourcesById = (state) => getStore(state).resourcesById;
export const getLastSyncDate = (state) => getStore(state).lastSyncDate;

export const getMemorizedRulesById = getResourcesById;
export const getMemorizedRuleById = (state, id) => getResourcesById(state).get(id);

export const getMemorizedRuleCoaStrings = createSelector(
  getMemorizedRulesById,
  categoriesSelectors.getCategoriesById,
  accountsSelectors.getAccountsById,
  (memorizedRules, _categoriesById, _accountsById) =>
    ImmutableMap().withMutations((coas) =>
      memorizedRules.forEach((memorizedRule) =>
        coas.set(
          chartOfAccountsUtils.getCoaId(memorizedRule.coa),
          chartOfAccountsSelectors.getCoaStringSelector(undefined, memorizedRule.coa, true),
        ),
      ),
    ),
);

export const getMemorizedRuleForTxn = createSelector(
  getMemorizedRulesById,
  (state, txn) => txn,
  (rulesById, txn) =>
    rulesById.find((rule) => rule.payee === txn.payee && (!rule.transactionType || rule.transactionType === txn.type)),
);

export const getMemorizedRuleApplicableTransactions = createSelector(
  (state) => transactionsSelectors.getTransactionsOfTypes(state, transactionsSelectors.TxnTypes.REGULAR_TXNS),
  selectorsHelpers.createValueCache((state, memorizedRule) => memorizedRule),
  selectorsHelpers.createValueCache((state, _memorizedRule, memorizedRuleOld = {}) => memorizedRuleOld),
  (transactions, memorizedRule, memorizedRuleOld) => {
    const payeeNormalized = memorizedRule.payee?.toLowerCase();
    const isPayeeChanged = memorizedRuleOld?.payee?.toLowerCase() !== payeeNormalized;
    const isCoaChanged = !chartOfAccountsUtils.coasAreEqual(memorizedRuleOld.coa, memorizedRule.coa);
    if (isPayeeChanged || isCoaChanged) {
      return transactions.filter(
        (transaction) =>
          !transactionsUtils.isSplitTxn(transaction) &&
          transaction.payee?.toLowerCase() === payeeNormalized &&
          transactionsUtils.coasAreDifferent(transaction.coa, memorizedRule.coa) &&
          (isPayeeChanged || !isCoaChanged || chartOfAccountsUtils.coasAreEqual(transaction.coa, memorizedRuleOld.coa)),
      );
    }
    return ImmutableMap();
  },
);

export const getMemorizedRulesByPayeeName = createSelector(
  getMemorizedRulesById,
  (_state, payeeName) => payeeName,
  (rulesById, payeeName) => rulesById.find(({ payee }) => payee === payeeName),
);
